import { atom, useAtom } from 'jotai';
import { Suspense, useContext, useEffect } from 'react';
import styles from './personal-details.module.scss';
import { Icon, ICONS_ENUM, Typography } from '@sanlam-glacier/app-common/components';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import { WidgetType } from 'modules/dashboard/types/widgets/widget.enums';
import { legalEntityFields, naturalPersonFields } from './personal-details-form-data.function';
import WidgetError from '../../../../atoms/widget-error/WidgetError';
import { useQuery } from '@tanstack/react-query';
import { getPersonalDetails } from 'bff/hooks/reporting/PersonalDetailsApi';
import { ClientType } from 'bff';
import { useParams } from 'react-router-dom';
import { clientTypeAtom } from 'stores/dashboard-store/dashboard-store';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';
import { useShouldHideInfo } from 'modules/dashboard/utils/widget/shouldHideInfo';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';

const hiddenFieldsAtom = atom<Set<string>>(new Set<string>());

export const PersonalDetailsWidget = () => {
  const shouldHide = useShouldHideInfo(WidgetType.personalDetails);
  const [hiddenFields, setHiddenFields] = useAtom(hiddenFieldsAtom);
  const { clientId } = useParams<{ clientId: string }>();
  const testComponentName = 'personalDetailsWidget';
  const hideIconTestId = 'hideIcon';
  const [_, setClientType] = useAtom(clientTypeAtom);
  const {
    data: personalDetails,
    isError,
    refetch,
    isFetching,
    isSuccess
  } = useQuery(getPersonalDetails({ clientId: Number(clientId) }));

  const isNatural = personalDetails?.clientType === ClientType.Natural;

  const fields = isNatural ? naturalPersonFields : legalEntityFields;

  const validData = isNatural
    ? personalDetails?.naturalPersonDetails
    : personalDetails?.legalEntityDetails;

  const context = useContext(WidgetContext);

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, []);

  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  useEffect(() => {
    setHiddenFields(new Set());
  }, [shouldHide, setHiddenFields]);

  const isFieldHidden = (key: string) => {
    if (shouldHide) {
      return !hiddenFields.has(key);
    }
    return hiddenFields.has(key);
  };

  const getDisplayValue = (key: string): string => {
    if (!validData) return '-';

    if (key.includes('.')) {
      return getNestedValue(key);
    }

    return getTopLevelValue(key);
  };

  const getNestedValue = (key: string): string => {
    const [parent, child] = key.split('.');

    const parentFieldValue =
      validData && parent && parent in validData
        ? validData[parent as keyof typeof validData]
        : undefined;

    // If parent exists and has the child property, return its value
    if (parentFieldValue && typeof parentFieldValue === 'object' && child) {
      const childValue = parentFieldValue[child] as string | undefined;
      return childValue?.toString() ?? '-';
    }

    return '-';
  };

  const getTopLevelValue = (key: string): string => {
    if (!validData) return '-';
    const value = key in validData ? validData[key as keyof typeof validData] : undefined;

    return value?.toString() ?? '-';
  };

  const toggleFieldVisibility = (key: string) => {
    setHiddenFields((prev) => {
      const newSet = new Set(prev);
      if (shouldHide) {
        if (newSet.has(key)) {
          newSet.delete(key);
        } else {
          newSet.add(key);
        }
      } else if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setClientType(personalDetails?.clientType);
  }, [isSuccess, setClientType]);

  return (
    <Suspense fallback={<WidgetLoader />}>
      {isFetching ? (
        <div className={styles.container}>
          <WidgetLoader />
        </div>
      ) : isError ? (
        <WidgetError onRefresh={refetch} isRefreshing={isFetching} />
      ) : (
        <div className={styles.widgetWrapper}>
          <ul className={styles.displayWrapper}>
            {fields.map(({ key, label, hideable }) => (
              <li
                key={key}
                className={styles.row}
                data-testid={testComponentName + label.replace(/^./, (c) => c.toUpperCase())}
              >
                <Typography
                  variant={Typography.VARIANT.B3}
                  weight={Typography.WEIGHT.MEDIUM}
                  className={styles.personalDetailsLabel}
                  data-testid={label.replace(/^./, (c) => c.toUpperCase())}
                >
                  {label}
                </Typography>
                <Typography
                  color={Typography.COLOR.TERTIARY}
                  variant={Typography.VARIANT.B3}
                  weight={Typography.WEIGHT.NORMAL}
                  className={styles.personalText}
                  data-testid={key.replace(/^./, (c) => c.toUpperCase()) + 'Value'}
                >
                  {hideable && isFieldHidden(key) ? (
                    <div data-testid={testComponentName + key} className={styles.hideField} />
                  ) : (
                    getDisplayValue(key)
                  )}
                </Typography>
                {hideable && (
                  <Typography
                    className={styles.hideIcon}
                    color={Typography.COLOR.PRIMARY}
                    onClick={() => toggleFieldVisibility(key)}
                    role='button'
                    tabIndex={0}
                  >
                    <Icon
                      data-testid={hideIconTestId + key}
                      icon={
                        isFieldHidden(key)
                          ? ICONS_ENUM.ACTION_2_VISIBILITY
                          : ICONS_ENUM.ACTION_2_VISIBILITY_OFF
                      }
                    />
                  </Typography>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Suspense>
  );
};

export default PersonalDetailsWidget;
